import { useMemo } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Phone, Mail, MapPin, Clock, ExternalLink } from 'lucide-react';
import { useClinicBranding } from '../context/ClinicBrandingContext';
import { buildFooterScheduleLines } from '../lib/scheduleDisplay';
import { buildGoogleMapsEmbedUrl, buildGoogleMapsSearchUrl } from '../lib/googleMaps';

export default function ContactPage() {
  const { info, schedule } = useClinicBranding();
  const scheduleLines = useMemo(() => buildFooterScheduleLines(schedule), [schedule]);

  const data = info || {
    name: 'PodoclinicAM',
    address: 'Imán, Miguel Hidalgo, 43998 Cd Sahagún, Hgo.',
    phone: '+52 1 595 108 9300',
    email: 'danielortegalozano@gmail.com',
    description: 'Clínica especializada en podología y cuidado de los pies',
  };

  const mapUrl = buildGoogleMapsSearchUrl(data.address);
  const mapEmbedUrl = buildGoogleMapsEmbedUrl(data.address);

  return (
    <div className="min-h-screen bg-background py-12" data-testid="contact-page">
      <div className="max-w-5xl mx-auto px-4">
        <div className="text-center mb-12">
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-foreground mb-4">Contacto</h1>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">{data.description}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="space-y-6">
            <Card className="border border-border">
              <CardContent className="p-6">
                <h2 className="font-heading text-xl font-semibold mb-6">Información de Contacto</h2>
                <div className="space-y-5">
                  <a
                    href={`tel:${String(data.phone).replace(/\s/g, '')}`}
                    className="flex items-start gap-4 group"
                    data-testid="contact-phone"
                  >
                    <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-primary/20 transition-colors">
                      <Phone className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Teléfono</p>
                      <p className="font-medium text-foreground group-hover:text-primary transition-colors">
                        {data.phone}
                      </p>
                    </div>
                  </a>

                  <a
                    href={`mailto:${data.email}`}
                    className="flex items-start gap-4 group"
                    data-testid="contact-email"
                  >
                    <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-primary/20 transition-colors">
                      <Mail className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Email</p>
                      <p className="font-medium text-foreground group-hover:text-primary transition-colors">
                        {data.email}
                      </p>
                    </div>
                  </a>

                  <a
                    href={mapUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-start gap-4 group"
                    data-testid="contact-address"
                  >
                    <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-primary/20 transition-colors">
                      <MapPin className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Dirección</p>
                      <p className="font-medium text-foreground group-hover:text-primary transition-colors">
                        {data.address}
                      </p>
                    </div>
                  </a>
                </div>
              </CardContent>
            </Card>

            <Card className="border border-border">
              <CardContent className="p-6">
                <h2 className="font-heading text-xl font-semibold mb-6 flex items-center gap-2">
                  <Clock className="w-5 h-5 text-primary" />
                  Horarios de Atención
                </h2>
                <p className="text-xs text-muted-foreground mb-4">
                  Los mismos horarios configurados en el panel de administración (Horarios).
                </p>
                <div className="space-y-2">
                  {scheduleLines.map((line, i) => (
                    <div
                      key={i}
                      className="flex justify-between items-center gap-4 py-2 border-b border-border last:border-0"
                    >
                      <span className="text-foreground text-sm leading-snug">{line}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Button asChild className="w-full rounded-full gap-2" data-testid="open-maps-btn">
              <a href={mapUrl} target="_blank" rel="noopener noreferrer">
                <MapPin className="w-4 h-4" />
                Ver en Google Maps
                <ExternalLink className="w-4 h-4" />
              </a>
            </Button>
          </div>

          <Card className="border border-border overflow-hidden h-[500px]">
            {mapEmbedUrl ? (
              <iframe
                title={`Ubicación de ${data.name}`}
                src={mapEmbedUrl}
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                data-testid="google-map"
              />
            ) : (
              <div className="flex h-full items-center justify-center p-6 text-center text-muted-foreground text-sm">
                No hay dirección configurada para mostrar el mapa.
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
